/**
 * PriceChangedDialog — reprice-at-submit confirmation (pricing rework
 * Phase E; shared by the family checkout AND the school forecast
 * submit/edit). Opens when a submit answers 409 `price_changed`: lists
 * each drifted line ("Item — was $X.XX, now $Y.YY") + the new order
 * total, then lets the user either place the order at the new prices
 * (resubmit with `acceptRepricing: true`) or go back to the cart
 * (which is patched to the fresh prices either way, so the UI never
 * shows a stale figure).
 *
 * Price DECREASES use the same dialog with friendlier copy.
 */

import { Button } from '@ordo/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import type { ReactElement } from 'react'
import { useMemo } from 'react'

import { buildPriceChangeView, type PriceChangeWire, type RepriceableLine } from '@/lib/orders/price-change'
import { formatCents } from '@/lib/school-orders/money'

export interface PriceChangedDialogProps {
  changes: PriceChangeWire[] | null
  /** The lines still in the cart — names + the new-total projection. */
  lines: readonly RepriceableLine[]
  submitting: boolean
  /** Primary action — resubmits with acceptRepricing: true. */
  onConfirm: () => void
  /** Secondary action — cart prices update, no resubmit. */
  onBackToCart: () => void
  /** Primary-button copy override (default: "Place order at new price"). */
  confirmLabel?: string
  /** Primary-button copy while `submitting` (default: "Placing order…"). */
  confirmPendingLabel?: string
  /** Secondary-button copy override (default: "Back to cart"). */
  cancelLabel?: string
}

export function PriceChangedDialog({
  changes,
  lines,
  submitting,
  onConfirm,
  onBackToCart,
  confirmLabel = 'Place order at new price',
  confirmPendingLabel = 'Placing order…',
  cancelLabel = 'Back to cart',
}: PriceChangedDialogProps): ReactElement {
  const view = useMemo(() => buildPriceChangeView(changes ?? [], lines), [changes, lines])
  const open = changes !== null && changes.length > 0

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onBackToCart()
        }
      }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{view.allDecreased ? 'Good news — the price went down' : 'Prices have changed'}</DialogTitle>
          <DialogDescription>
            {view.allDecreased
              ? 'Some items in your cart now cost less than when you added them.'
              : 'Some prices changed since you added these items. Review the new prices before placing your order.'}
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y text-sm">
          {view.lines.map((line) => (
            <li key={line.menuItemId} className="flex items-center justify-between gap-3 py-2.5">
              <span className="min-w-0 flex-1 truncate font-medium text-foreground">{line.name}</span>
              <span className="flex shrink-0 items-center gap-2 tabular-nums">
                <span className="text-muted-foreground line-through">{formatCents(line.oldCents, view.currency)}</span>
                <span className="font-semibold text-foreground">{formatCents(line.newCents, view.currency)}</span>
              </span>
            </li>
          ))}
        </ul>
        {view.newTotalCents !== null ? (
          <div className="flex items-center justify-between border-t pt-3 text-sm">
            <span className="font-medium text-muted-foreground">New total</span>
            <span className="text-base font-semibold tabular-nums text-foreground">
              {formatCents(view.newTotalCents, view.currency)}
            </span>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onBackToCart} disabled={submitting}>
            {cancelLabel}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={submitting}>
            {submitting ? confirmPendingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
